import { useState } from 'react'
import { Trans } from 'react-i18next'
import clsx from 'clsx'

import { AppBreakpoint } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { BuilderDialog, IActionFormProps } from '@makyoapp/makyo-common'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant, Field, Label, SelectDropdown, Slider, Switch, Tip, useTip } from '@makyoapp/makyo-ui'

import { useForm } from '@/lib/use-form'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useVariableTextSingleCell } from '@/hooks/__project__/useVariableTextSingleCell'
import { BUILDER_DEVICES } from '@/shared/builder-constants'
import { isEmpty } from '@/shared/utils'

function DeviceTab({ device, isActive, onClick }: { device: AppBreakpoint; isActive: boolean; onClick: () => void }) {
  const { Icon, label } = BUILDER_DEVICES[device]
  const { x, y, strategy, refs, context, placement, isOpen, arrowRef, getReferenceProps, getFloatingProps } = useTip()

  return (
    <>
      <button
        ref={refs.setReference}
        onClick={onClick}
        className={clsx(
          'h-12 aspect-square flex items-center justify-center transition-colors',
          'bg-slate-800 hover:bg-slate-700',
          isActive && 'bg-slate-900',
          device === 'phone' && 'rounded-l',
          device === 'tv' && 'rounded-r'
        )}
        {...getReferenceProps()}
      >
        <Icon className={clsx('w-5 h-5', isActive ? 'text-white' : 'text-slate-400')} />
      </button>
      {isOpen && (
        <Tip
          x={x}
          y={y}
          placement={placement}
          strategy={strategy}
          ref={refs.setFloating}
          {...getFloatingProps()}
          context={context}
          arrowRef={arrowRef}
        >
          {label}
        </Tip>
      )}
    </>
  )
}

const RenderDialogOption = ({ dialog }: { dialog: BuilderDialog }) => {
  const dialogName = useVariableTextSingleCell(dialog.name)

  return (
    <span className='flex items-center'>
      {dialog?.icon && <MaterialIcon size={20} icon={dialog.icon ?? 'info'} className='mr-2' />}
      <span className={clsx(!dialog.id && 'text-light')}>{dialogName}</span>
    </span>
  )
}

const defaultDialogSettings = {
  showIcon: {
    phone: false,
    'phone-landscape': false,
    tablet: false,
    'tablet-landscape': false,
    desktop: false,
    'desktop-landscape': false,
  },
  showDivider: {
    phone: false,
    'phone-landscape': false,
    tablet: false,
    'tablet-landscape': false,
    desktop: false,
    'desktop-landscape': false,
  },
  width: {
    phone: 80,
    'phone-landscape': 45,
    tablet: 65,
    'tablet-landscape': 40,
    desktop: 35,
    'desktop-landscape': 35,
  },
  contentAlignment: {
    phone: 'center',
    'phone-landscape': 'center',
    tablet: 'center',
    'tablet-landscape': 'center',
    desktop: 'center',
    'desktop-landscape': 'center',
  },
  footerAlignment: {
    phone: 'center',
    'phone-landscape': 'center',
    tablet: 'center',
    'tablet-landscape': 'center',
    desktop: 'center',
    'desktop-landscape': 'center',
  },
}

export default function ActFormShowDialog({ onSubmit, onDismiss, initialData }: IActionFormProps) {
  const { dialogs } = useAppBuilderContext()

  const dialogOptions: BuilderDialog[] = [
    {
      id: '',
      name: 'Select Dialog',
    } as BuilderDialog,
    ...(dialogs || []),
  ]

  const { form, onSubmit: onFormSubmit } = useForm({
    defaultValues: {
      dialog: initialData?.dialog || dialogs[0].id || '',
    },
  })

  const handleSubmit = onFormSubmit((data) => {
    onSubmit(data)
  })

  const selectedDialog = form.getValues('dialog')
  const invalidForm = isEmpty(selectedDialog) || !!form.errors

  return (
    <div>
      <div className='p-4'>
        <Field>
          <Label required>Select Dialog</Label>

          <div className='flex-1'>
            <SelectDropdown
              multiple={false}
              options={dialogOptions}
              optionLabel={(opt) => opt.name}
              value={dialogOptions.find(({ id }) => id === form.getValues('dialog'))}
              renderOption={(opt) => <RenderDialogOption dialog={opt} />}
              onChange={(dialog) => {
                form.setValues('dialog', dialog.id)
              }}
            />

            <div className='mt-1 text-m-sm red h-[17px]'>{form.errors?.dialog}</div>
          </div>
        </Field>
      </div>

      <div className='px-4 py-2 border-t bdr flex justify-between items-center'>
        <Button variant={ButtonVariant.Outlined} onClick={onDismiss}>
          <Trans>Cancel</Trans>
        </Button>
        <Button onClick={handleSubmit} disabled={invalidForm}>
          <Trans>Save</Trans>
        </Button>
      </div>
    </div>
  )
}
